import type { IUiThreeDPresentation } from './Interfaces';
import type { IPresentationChangeHandler } from './Interfaces';
import * as THREE from 'three';
import type { IViewPortController } from '@/engine/app/interfaces';
import { DisposablesBasket } from '@/engine/Utilities/DisposablesBasket';
import { reaction } from 'mobx';
import type { ViewsService } from '@/engine/app/Views.service';
import type { Cursor3D } from '@/engine/app/models/viewEntity/view.entity.ts';
import { AutoDisposer } from '@/engine/Utilities/AutoDisposer';

export class GhostCursorPresentation implements IUiThreeDPresentation {
  private readonly _mesh: THREE.Object3D;

  private readonly _disposables = new DisposablesBasket();

  public constructor(
    changeHandler: IPresentationChangeHandler,
    viewPortController: IViewPortController,
    viewsService: ViewsService
  ) {
    const cameraController = viewPortController.getCameraController();

    const roiRadius = 125; // TODO: [chebotarev] applicable for heart anatomy as 125 is ~radius of the all objects on the scene, we should get true scene bounding box or sphere dimension instead
    const cursorMaxZoomFactor = 0.5; // cursor zoom factor when we are roiRadius away from center of roi

    const coneRadius = 6;
    const coneHeight = coneRadius * 3;
    const cylinderRadius = coneRadius / 3;
    const cylinderHeight = coneHeight / 2;

    const geometry1 = new THREE.ConeGeometry(coneRadius, coneHeight, 32).translate(0, -coneHeight / 2, 0);
    const geometry2 = new THREE.CylinderGeometry(cylinderRadius, cylinderRadius, cylinderHeight, 32).translate(
      0,
      -coneHeight - cylinderHeight / 2,
      0
    );

    // cursor's inner part
    const matInner = new THREE.MeshPhongMaterial({
      color: 0x333333,
      specular: 0x555555,
      transparent: true,
      opacity: 0.5,
      depthTest: false
    }); // to make it visible when inside other objects
    const meshInner = new THREE.Group().add(new THREE.Mesh(geometry1, matInner), new THREE.Mesh(geometry2, matInner));
    meshInner.renderOrder = 10000;

    // cursor's outer part
    const matOuter = new THREE.MeshPhongMaterial({
      color: 0xffffff,
      specular: 0x555555,
      transparent: true
    });
    const meshOuter = new THREE.Group().add(new THREE.Mesh(geometry1, matOuter), new THREE.Mesh(geometry2, matOuter));
    meshOuter.renderOrder = meshInner.renderOrder + 1; // to make it be on top of inner part when both are visible

    // resulting combined cursor
    const meshCursor = new THREE.Group().add(meshInner, meshOuter);

    // pointer at the screen edge, that is being rendered when cursor is outside of the view
    const matPointer = new THREE.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      depthTest: false
    }); // to make it always visible
    const meshPointer = new THREE.Mesh(geometry1, matPointer);
    meshPointer.renderOrder = meshOuter.renderOrder + 1; // to make it be on very top
    meshPointer.visible = false;

    this._disposables.add(geometry1, geometry2, matInner, matOuter, matPointer);
    this._mesh = new THREE.Group().add(meshCursor, meshPointer);
    this._mesh.visible = false;

    const dummyThreeDPoint = new THREE.Vector3();
    const coneBottomDir = new THREE.Vector3(0, 1, 0);
    const quaternion = new THREE.Quaternion();

    const reactionDisposer = new AutoDisposer(
      reaction(
        () => viewsService.currentView?.getGhostCursor(),
        (cursor: Cursor3D | undefined) => {
          if (cursor) {
            this._mesh.visible = true;
            const clampedCoords = cameraController.clampPointToFrame(cursor.coordinates);
            if (clampedCoords) {
              meshCursor.visible = false;
              meshPointer.visible = true;
              // cone position and orientation
              this._mesh.position.copy(clampedCoords);
              const cursorDir = cursor.coordinates.clone().sub(clampedCoords).normalize();
              quaternion.setFromUnitVectors(coneBottomDir, cursorDir);
              this._mesh.setRotationFromQuaternion(quaternion);
              const frameDimensions = cameraController.getFrameDimensions();
              const scaleFactor = Math.max(0.5, Math.min(1.0, frameDimensions.y / 1000));
              this._mesh.scale.setScalar(cameraController.getCameraZoomFactor() * scaleFactor);
            } else {
              meshPointer.visible = false;
              meshCursor.visible = true;
              // cone position and orientation
              this._mesh.position.copy(cursor.coordinates);
              quaternion.setFromUnitVectors(coneBottomDir, cursor.direction);
              this._mesh.setRotationFromQuaternion(quaternion);
              // cone size
              const viewRay = new THREE.Ray(
                cameraController.getCameraPosition(),
                cameraController.getCameraDirection()
              );
              const distC = viewRay.origin.distanceTo(
                viewRay.closestPointToPoint(cameraController.getFitCenterPoint(), dummyThreeDPoint)
              );
              const distP = viewRay.origin.distanceTo(
                viewRay.closestPointToPoint(cursor.coordinates, dummyThreeDPoint)
              );
              const dist = distC - distP; // signed distance from center to point in camera view ray
              const scaleFactor = Math.max(0.5, Math.min(2.0, 1 + (dist * cursorMaxZoomFactor) / roiRadius));
              this._mesh.scale.setScalar(cameraController.getCameraZoomFactor() * scaleFactor);
            }
          } else {
            this._mesh.visible = false;
          }
          changeHandler.handlePresentationChange();
        }
      )
    );

    this._disposables.add(reactionDisposer);
  }

  public get3dObject(): THREE.Object3D {
    return this._mesh;
  }

  public dispose(): void {
    this._disposables.dispose();
  }
}
